import React, { useCallback, useContext, useEffect, useState } from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import Alert from "react-bootstrap/Alert";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import { Phone } from "react-bootstrap-icons";
import { WindowDesktop } from "react-bootstrap-icons";
import { Formik, Form as FormikForm } from "formik";
import { createWorkerFactory, useWorker } from "@shopify/react-web-worker";
import { useLocalStorage } from "usehooks-ts";
import { UserAuthenticatedContext } from "../components/profile";
import Loading from "../components/loading";
import InventoryInboundCard from "../components/inbound/inbound-card";
import InboundNotesModal from "../components/inbound/inbound-notes-modal";
import InboundNewItemModal from "../components/inbound/inbound-new-item-modal";
import { InventoryItem } from "../external";
import ListGroup from "react-bootstrap/ListGroup";
import Card from "react-bootstrap/Card";
import Navbar from "react-bootstrap/Navbar";
import ConfirmationModal from "../components/confirmation-modal";
import { useMediaQuery } from "usehooks-ts";
import InventoryInboundModal from "../components/inbound/inbound-modal";
import { useQueryParams, StringParam } from "use-query-params";

const createInventoryWorker = createWorkerFactory(
  () => import("../workers/inventory")
);

const preventSelectStyle: React.CSSProperties = {
  userSelect: "none",
  WebkitUserSelect: "none",
  MozUserSelect: "none",
  msUserSelect: "none",
  WebkitTouchCallout: "none",
};

interface MobileInboundViewProps {
  items: InventoryItem[];
  onToggleArrived: (index: number) => void;
  onMarkAllArrived: () => void;
  onShowNotes: (index: number) => void;
  onShowCardModal: (index: number) => void;
  inboundProcessed: boolean;
  onAddNewItem: () => void;
  onSubmit: () => void;
  itemsConfirmedOverride: boolean;
  setItemsConfirmedOverride: (value: boolean) => void;
  isSubmitting: boolean;
}

const MobileInboundView: React.FC<MobileInboundViewProps> = ({
  items,
  onToggleArrived,
  onMarkAllArrived,
  onShowNotes,
  onShowCardModal,
  inboundProcessed,
  onAddNewItem,
  onSubmit,
  itemsConfirmedOverride,
  setItemsConfirmedOverride,
  isSubmitting,
}) => {
  const [showCompleted, setShowCompleted] = useState(true);
  const [expandedPallets, setExpandedPallets] = useState<
    Record<string, boolean>
  >({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Group items by pallet
  const pallets = items.reduce((acc, item) => {
    const palletId = item.pallet || "default";
    if (!acc[palletId]) {
      acc[palletId] = [];
    }
    acc[palletId].push(item);
    return acc;
  }, {} as Record<string, InventoryItem[]>);

  // Initialize pallets as expanded ONLY for new pallets
  useEffect(() => {
    const palletIds = Object.keys(pallets);
    const hasNewPallets = palletIds.some(
      (id) => expandedPallets[id] === undefined
    );

    if (hasNewPallets) {
      setExpandedPallets((prev) => {
        const newState = { ...prev };
        palletIds.forEach((id) => {
          if (newState[id] === undefined) {
            newState[id] = true; // Only set new pallets to expanded
          }
        });
        return newState;
      });
    }
  }, [pallets, expandedPallets]);

  const handleHeaderClick = useCallback(
    (palletId: string, e: React.MouseEvent) => {
      // Check if the click was directly on the header or its text elements
      const target = e.target as HTMLElement;
      if (target.tagName === "BUTTON" || target.closest("button")) {
        return;
      }

      setExpandedPallets((prev) => {
        return { ...prev, [palletId]: !prev[palletId] };
      });
    },
    []
  );

  const allConfirmed = items.every((item) => item.arrivalConfirmed);
  const someUnconfirmed = items.some((item) => !item.arrivalConfirmed);

  const buttonWidth = "15%";

  return (
    <>
      <Container
        fluid
        className="p-2"
        style={{
          maxHeight: "100vh",
          overflowY: "auto",
          touchAction: "manipulation",
        }}
      >
        <Button
          variant="outline-secondary"
          onClick={() => setShowCompleted(!showCompleted)}
          className="w-100 mb-2"
        >
          {showCompleted ? "Hide Completed Pallets" : "Show All Pallets"}
        </Button>

        {Object.entries(pallets).map(([palletId, palletItems]) => {
          const arrivedCount = palletItems.filter(
            (i) => i.arrivalConfirmed
          ).length;
          const allArrived = arrivedCount === palletItems.length;

          if (!showCompleted && allArrived) return null;

          return (
            <Card
              key={palletId}
              className="mb-3"
              border={allArrived ? "success" : "warning"}
            >
              <Card.Header
                className={`d-flex justify-content-between align-items-center ${
                  allArrived
                    ? "bg-success bg-opacity-10"
                    : "bg-warning bg-opacity-10"
                }`}
                onClick={(e) => handleHeaderClick(palletId, e)}
                style={{ cursor: "pointer", ...preventSelectStyle }}
              >
                <div className="d-flex align-items-center">
                  <span className="me-2">
                    {expandedPallets[palletId] ? "▼" : "►"}
                  </span>
                  <strong>Pallet #{palletId}</strong>
                </div>
                <div className="d-flex align-items-center position-relative">
                  <Button
                    variant={allArrived ? "success" : "warning"}
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent any parent click events
                      setShowConfirmModal(true);
                    }}
                    disabled={allArrived || inboundProcessed}
                    className="d-flex align-items-center"
                    style={{ zIndex: 2 }} // Ensure button is above other elements
                  >
                    <span className="me-2">
                      {arrivedCount}/{palletItems.length} ✅
                    </span>
                    {!allArrived && !inboundProcessed && <span>MARK ALL</span>}
                  </Button>
                </div>
              </Card.Header>

              {expandedPallets[palletId] && (
                <ListGroup variant="flush">
                  {palletItems.map((item, idx) => {
                    // Find the original index in the main items array for callbacks
                    const originalIndex = items.findIndex((i) => i === item);

                    return (
                      <ListGroup.Item
                        key={item.id}
                        action
                        onClick={() => onShowCardModal(originalIndex)}
                        className={[
                          "d-flex",
                          "justify-content-between",
                          "align-items-center",
                          "user-select-none",
                          "position-relative",
                          "overflow-hidden",
                        ].join(" ")}
                        style={{ paddingRight: 0 }}
                      >
                        {/* Content area */}
                        <div
                          className="flex-grow-1"
                          style={{
                            marginRight: `calc(${buttonWidth} + 0.25em)`,
                          }}
                        >
                          <strong>{item.clientItemId}</strong> | Qty:{" "}
                          {item.quantity ?? 1}
                          <br />
                          <small>{item.name}</small>
                        </div>

                        {/* Actions area */}
                        <div className="d-flex align-items-center">
                          {/* Confirmation button with comment indicator when applicable */}
                          <Button
                            variant={
                              item.arrivalConfirmed ? "success" : "warning"
                            }
                            disabled={inboundProcessed}
                            className={[
                              "p-0",
                              "border-0",
                              "rounded-0",
                              "d-flex",
                              "align-items-center",
                              "justify-content-center",
                              inboundProcessed ? "opacity-50" : "",
                            ].join(" ")}
                            style={{
                              position: "absolute",
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: buttonWidth,
                              minWidth: "30px",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!inboundProcessed) {
                                onToggleArrived(originalIndex);
                              }
                            }}
                            title={
                              (item.arrivalConfirmed
                                ? "Confirmed"
                                : "Not yet confirmed") +
                              (item.comments ? " (has comments)" : "")
                            }
                          >
                            {/* Show notes icon if there are comments, otherwise show check/question mark */}
                            {item.comments
                              ? "📝"
                              : item.arrivalConfirmed
                              ? "✓"
                              : "?"}
                          </Button>
                        </div>
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
              )}
            </Card>
          );
        })}

        {!inboundProcessed && (
          <Card className="position-sticky bottom-0 p-2 border-top">
            {someUnconfirmed && !allConfirmed && (
              <Alert variant="warning" className="p-2 mb-2">
                <Form.Check
                  type="switch"
                  id="override-confirm"
                  label="Override unconfirmed items"
                  checked={itemsConfirmedOverride}
                  onChange={(e) => setItemsConfirmedOverride(e.target.checked)}
                />
              </Alert>
            )}

            <Row className="g-2">
              <Col>
                <Button
                  variant="secondary"
                  onClick={onAddNewItem}
                  disabled={isSubmitting}
                  className="w-100"
                >
                  Add New Item
                </Button>
              </Col>
              <Col>
                <Button
                  variant="success"
                  onClick={onSubmit}
                  disabled={
                    isSubmitting || (!allConfirmed && !itemsConfirmedOverride)
                  }
                  className="w-100"
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
              </Col>
            </Row>
          </Card>
        )}
      </Container>

      <ConfirmationModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={onMarkAllArrived}
        title="Confirm Mark All Items"
        message={`Are you sure you want to mark all ${
          Object.values(pallets).flat().length
        } items as confirmed?`}
        subMessage={`This will update all pending items on this pallet.`}
        confirmButtonText="Mark All"
        confirmButtonVariant="warning"
      />
    </>
  );
};

const InboundLoadPage = () => {
  const userAuthenticatedContext = useContext(UserAuthenticatedContext);
  const inventoryWorker = useWorker(createInventoryWorker);
  const [queryParams, setQueryParams] = useQueryParams({
    loadRefId: StringParam,
  });

  const [isLoadReferencesLoaded, setIsLoadReferencesLoaded] =
    useState<boolean>(false);
  const [isLoadReferenceItemsLoaded, setIsLoadReferenceItemsLoaded] =
    useState<boolean>(false);
  const [loadReferences, setLoadReferences] = useState<
    { loadReference: string; expectedDeliveryDate: string | null }[]
  >([]);
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [selectedResult, setSelectedResult] = useLocalStorage<InventoryItem[]>(
    "inventory-item-values",
    []
  );
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showNotesModal, setShowNotesModal] = useState<boolean>(false);
  const [showNewItemModal, setShowNewItemModel] = useState<boolean>(false);
  const [filterCompleted, setFilterCompleted] = useState<boolean>(false);
  const [notesSaved, setNotesSaved] = useState<boolean>(false);
  const [index, setIndex] = useState<number>(0);
  const [itemsConfirmedOverride, setItemsConfirmedOverride] =
    useState<boolean>(false);
  const [inboundProcessed, setInboundProcessed] = useState<boolean>(false);
  const [showSavingError, setShowSavingError] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  const isSmallScreen = useMediaQuery("(max-width: 768px)");
  const [isMobileView, setIsMobileView] = useState<boolean>(isSmallScreen);

  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    setIsMobileView(isSmallScreen);
  }, [isSmallScreen]);

  const handleLoadReferenceFromParams = useCallback(
    async (loadRefId: string) => {
      setLoadError(null);
      setShowSuccess(false);
      setShowSavingError(false);
      setIsLoadReferenceItemsLoaded(false);
      setSelectedOption(loadRefId);

      try {
        const items = await inventoryWorker.getInventoryItemsByLoadReferences(
          loadRefId,
          userAuthenticatedContext.token!
        );

        if (!items || items.length === 0) {
          setLoadError(
            `No inventory items found for load reference "${loadRefId}"`
          );
          setSelectedResult([]);
          return;
        }

        setSelectedResult(items);
        setIsLoadReferenceItemsLoaded(true);

        const alreadyProcessed = items.some(
          (item) => item.inboundCompleted === true
        );
        setInboundProcessed(alreadyProcessed);
      } catch (error: any) {
        console.error("Error loading inventory items:", error);

        if (error.response?.status === 404) {
          setLoadError(`Load reference "${loadRefId}" not found`);
        } else {
          setLoadError(`Error: ${error.message || "Unknown error"}`);
        }

        setSelectedResult([]);
      }
    },
    [inventoryWorker, userAuthenticatedContext.token, setSelectedResult]
  );

  const handleOptionSelect = useCallback(
    async (event: any) => {
      setShowSuccess(false);
      setShowSavingError(false);
      setLoadError(null);
      setInboundProcessed(false);
      setItemsConfirmedOverride(false);
      setIsLoadReferenceItemsLoaded(false);
      const option = event.target.value;
      setSelectedOption(option);
      setQueryParams({ loadRefId: option }, "replaceIn");

      const items: InventoryItem[] =
        await inventoryWorker.getInventoryItemsByLoadReferences(
          option,
          userAuthenticatedContext.token!
        );

      if (!items || items.length === 0) {
        setLoadError(`No inventory items found for load reference "${option}"`);
        setSelectedResult([]);
        setIsLoadReferenceItemsLoaded(false);
        return;
      }

      const validSavedItems = Array.isArray(selectedResult)
        ? selectedResult
        : [];

      const mergedItems = items.map((freshItem) => {
        const matchingSavedItem = validSavedItems.find(
          (savedItem) => savedItem.id === freshItem.id
        );

        if (matchingSavedItem) {
          return {
            ...freshItem,
            comments: matchingSavedItem.comments || freshItem.comments,
            arrivalConfirmed:
              matchingSavedItem.arrivalConfirmed !== undefined
                ? matchingSavedItem.arrivalConfirmed
                : freshItem.arrivalConfirmed,
          };
        }

        return freshItem;
      });

      setSelectedResult(mergedItems);
      setIsLoadReferenceItemsLoaded(true);

      items.forEach((item: InventoryItem) => {
        if (item.inboundCompleted) {
          setInboundProcessed(true);
        }
      });
    },
    [
      inventoryWorker,
      selectedResult,
      setSelectedResult,
      userAuthenticatedContext.token,
      setQueryParams,
    ]
  );

  const handleShowModal = (index: number) => {
    setIndex(index);
    setShowModal(true);
  };

  const handleShowNotesModal = (index: number, data: InventoryItem) => {
    setShowNotesModal(true);
    setIndex(index);
  };

  const handleCloseNotesModal = () => {
    setNotesSaved(false);
    setShowNotesModal(false);
    setIndex(0);
  };

  const handleShowNewItemModal = () => {
    setShowNewItemModel(true);
    setIndex(selectedResult.length);
  };

  const handleCloseNewItemModal = () => {
    setShowNewItemModel(false);
    setIndex(0);
  };

  const handleAddInventoryItem = (item: InventoryItem) => {
    const updatedItems = [...selectedResult, item];
    setSelectedResult(updatedItems);
  };

  const handleConfirmArrival = async (
    index: number,
    value: boolean,
    setFieldValue: (field: string, value: any) => void
  ) => {
    setFieldValue(`[${index}].arrivalConfirmed`, value);
  };

  const handleNotesUpdate = (
    notes: string,
    setFieldValue: (field: string, value: any) => void,
    index: number
  ) => {
    const newItems = [...selectedResult];
    newItems[index].comments = notes;
    setSelectedResult(newItems);
    setNotesSaved(true);
    setTimeout(() => {
      setShowNotesModal(false);
      setNotesSaved(false);
    }, 1000);
  };

  const checkArrivalConfirmed = (values: InventoryItem[]) => {
    return values.every((item) => item.arrivalConfirmed);
  };

  // Mobile-specific handlers
  const handleMobileToggleArrived = (index: number) => {
    const newItems = [...selectedResult];
    newItems[index].arrivalConfirmed = !newItems[index].arrivalConfirmed;
    setSelectedResult(newItems);
  };

  const handleMobileMarkAllArrived = () => {
    const newItems = selectedResult.map((item) => ({
      ...item,
      arrivalConfirmed: true,
    }));
    setSelectedResult(newItems);
  };

  const handleMobileShowNotes = (index: number) => {
    setIndex(index);
    setShowNotesModal(true);
  };

  const handleShowCardModal = (index: number) => {
    if ("vibrate" in navigator) {
      navigator.vibrate(50);
    }
    setIndex(index);
    setShowModal(true);
  };

  const handleMobileSubmit = async () => {
    let error = false;
    setShowSuccess(false);
    setShowSavingError(false);

    await Promise.all(
      selectedResult.map(async (value) => {
        if (value.arrivalConfirmed) {
          value.arrivalDate = new Date().toISOString();
        }

        value.inboundCompleted = true;

        try {
          await inventoryWorker.updateInventoryItem(
            value.id,
            value,
            userAuthenticatedContext.token!
          );
        } catch (updateError) {
          console.error("Error Updating Item", updateError);
          error = true;
        }
      })
    );

    if (error) {
      setShowSavingError(true);
    } else {
      setSelectedOption("");
      setSelectedResult([]);
      setIsLoadReferenceItemsLoaded(false);
      setShowSuccess(true);
    }
  };

  useEffect(() => {
    if (userAuthenticatedContext.token) {
      (async () => {
        const references = await inventoryWorker.getLoadReferences(
          userAuthenticatedContext.token!,
          filterCompleted
        );
        setShowSavingError(false);
        setIsLoadReferencesLoaded(true);
        setLoadReferences(references);
      })();
    }
  }, [userAuthenticatedContext, inventoryWorker, filterCompleted]);

  useEffect(() => {
    if (queryParams.loadRefId && userAuthenticatedContext.token) {
      setSelectedOption(queryParams.loadRefId);
      handleLoadReferenceFromParams(queryParams.loadRefId);
    }
  }, [
    queryParams.loadRefId,
    userAuthenticatedContext.token,
    handleLoadReferenceFromParams,
  ]);

  // Move permission check outside the effect
  if (!userAuthenticatedContext.scopes?.includes("edit:inventory-item")) {
    return (
      <Container>
        <Row>
          <Col className="mb-2">No permission to Inbound</Col>
        </Row>
      </Container>
    );
  }

  return (
    <>
      {loadError ? (
        <Alert variant="warning" className="mt-3">
          <Alert.Heading>Warning</Alert.Heading>
          <p>{loadError}</p>
          {queryParams.loadRefId && (
            <div className="mt-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  setQueryParams({ loadRefId: undefined });
                  setLoadError(null);
                  setSelectedOption("");
                }}
              >
                Clear....
              </Button>
            </div>
          )}
        </Alert>
      ) : (
        <Container>
          <Navbar className="mb-3">
            <Navbar.Brand>
              <h1 className="m-0">Inbound</h1>
            </Navbar.Brand>
            <Navbar.Collapse className="justify-content-end">
              <Button
                variant="outline-secondary"
                onClick={() => setIsMobileView(!isMobileView)}
              >
                {isMobileView ? <Phone /> : <WindowDesktop />}
              </Button>
            </Navbar.Collapse>
          </Navbar>
          {!isLoadReferencesLoaded ? (
            <Spinner style={{ marginLeft: "0.5em" }} size="sm" />
          ) : (
            <>
              <Row className="mb-4">
                <Col>
                  <>
                    {loadReferences.length > 0 ? (
                      <Container fluid className="px-0 mx-0">
                        <Form.Group as={Row} className="mx-0">
                          <Col xs={8} sm={6} md={5} className="pb-0 ps-0">
                            <Form.Select
                              aria-label="Default select example"
                              value={selectedOption}
                              onChange={handleOptionSelect}
                            >
                              <option value="" disabled hidden>
                                Select Load..
                              </option>
                              {loadReferences.map((reference, index) => (
                                <option
                                  key={index}
                                  value={reference.loadReference}
                                >
                                  {`${reference.loadReference} - ${reference.expectedDeliveryDate}`}
                                </option>
                              ))}
                            </Form.Select>
                          </Col>
                          <Col
                            xs={4}
                            sm={6}
                            md={7}
                            className="d-flex justify-content-end"
                          >
                            <div className="d-flex flex-column align-items-center">
                              <Form.Switch
                                id="switch-completed"
                                onChange={(e) => {
                                  setFilterCompleted(e.target.checked);
                                  setSelectedOption("");
                                  setIsLoadReferenceItemsLoaded(false);
                                  setSelectedResult([]);
                                }}
                                label="" // Remove the default label
                              />
                              <Form.Text
                                className="text-center mt-1"
                                style={{ minWidth: "70px" }}
                              >
                                {filterCompleted ? "Complete" : "Unsubmitted"}
                              </Form.Text>
                            </div>
                          </Col>
                        </Form.Group>
                      </Container>
                    ) : (
                      "Could not find any Load References"
                    )}
                  </>
                </Col>
              </Row>
              {showSuccess ? (
                <Row>
                  <Col xs={12}>
                    <Alert variant="success">
                      Inbound has been marked as completed.
                    </Alert>
                  </Col>
                </Row>
              ) : null}
              {!isLoadReferenceItemsLoaded ? (
                selectedOption !== "" ? (
                  <Row>
                    <Col>
                      <Spinner style={{ marginLeft: "0.5em" }} size="sm" />
                    </Col>
                  </Row>
                ) : null
              ) : (
                <>
                  {isMobileView ? (
                    <MobileInboundView
                      items={selectedResult}
                      onToggleArrived={handleMobileToggleArrived}
                      onMarkAllArrived={handleMobileMarkAllArrived}
                      onShowNotes={handleMobileShowNotes}
                      onShowCardModal={handleShowCardModal}
                      inboundProcessed={inboundProcessed}
                      onAddNewItem={handleShowNewItemModal}
                      onSubmit={handleMobileSubmit}
                      itemsConfirmedOverride={itemsConfirmedOverride}
                      setItemsConfirmedOverride={setItemsConfirmedOverride}
                      isSubmitting={false}
                    />
                  ) : (
                    <Row>
                      <Col xs={12}>
                        <h2>{selectedResult[0]?.loadReferenceId}</h2>

                        {inboundProcessed ? (
                          <Alert variant="warning">
                            This Inbound has already been processed. You can
                            only view the processed data.
                          </Alert>
                        ) : null}

                        {showSavingError ? (
                          <Alert variant="danger">
                            There was an error saving the data. Please try
                            again.
                          </Alert>
                        ) : null}
                      </Col>
                      <Col xs={12}>
                        <Formik
                          initialValues={selectedResult}
                          enableReinitialize={true}
                          onSubmit={async (values, { resetForm }) => {
                            let error = false;
                            setShowSuccess(false);
                            setShowSavingError(false);

                            await Promise.all(
                              values.map(async (value) => {
                                if (value.arrivalConfirmed) {
                                  value.arrivalDate = new Date().toISOString();
                                }

                                value.inboundCompleted = true;

                                try {
                                  await inventoryWorker.updateInventoryItem(
                                    value.id,
                                    value,
                                    userAuthenticatedContext.token!
                                  );
                                } catch (updateError) {
                                  console.error(
                                    "Error Updating Item",
                                    updateError
                                  );
                                  error = true;
                                }
                              })
                            );

                            if (error) {
                              setShowSavingError(true);
                            } else {
                              setSelectedOption("");
                              setSelectedResult([]);
                              setIsLoadReferenceItemsLoaded(false);
                              setShowSuccess(true);
                              resetForm();
                            }
                          }}
                        >
                          {({
                            values,
                            isValid,
                            isSubmitting,
                            setFieldValue,
                            setValues,
                          }) => (
                            <FormikForm>
                              <Row xs={1} sm={2} md={4}>
                                {values && values.length > 0
                                  ? selectedResult
                                      .map((result, index) => ({
                                        ...result,
                                        originalIndex: index,
                                      }))
                                      .sort((a, b) =>
                                        a.name.localeCompare(b.name)
                                      )
                                      .map((result) => (
                                        <InventoryInboundCard
                                          key={result.originalIndex}
                                          index={result.originalIndex}
                                          values={result}
                                          onShowModal={handleShowModal}
                                          onShowNotesModal={
                                            handleShowNotesModal
                                          }
                                          onHandleConfirmArrival={
                                            handleConfirmArrival
                                          }
                                          disabled={isSubmitting}
                                          setFieldValue={setFieldValue}
                                          inboundProcessed={inboundProcessed}
                                          editable={true}
                                        />
                                      ))
                                  : null}
                              </Row>
                              {!inboundProcessed ? (
                                <>
                                  {checkArrivalConfirmed(values) ? (
                                    ""
                                  ) : (
                                    <>
                                      <Alert variant="warning">
                                        <Form.Check
                                          type="checkbox"
                                          label="Not all items have been confirmed. Please check to override to allow submission. Any items not confirmed will be marked as not arrived!"
                                          checked={itemsConfirmedOverride}
                                          onChange={(e) =>
                                            setItemsConfirmedOverride(
                                              e.target.checked
                                            )
                                          }
                                        />
                                      </Alert>
                                    </>
                                  )}
                                  <Button
                                    style={{ marginRight: "10px" }}
                                    disabled={isSubmitting}
                                    onClick={handleShowNewItemModal}
                                  >
                                    Add New Item
                                  </Button>
                                  <Button
                                    type="submit"
                                    disabled={
                                      !isValid ||
                                      isSubmitting ||
                                      (!checkArrivalConfirmed(values) &&
                                        !itemsConfirmedOverride)
                                    }
                                  >
                                    Submit
                                  </Button>
                                </>
                              ) : null}
                            </FormikForm>
                          )}
                        </Formik>
                      </Col>
                    </Row>
                  )}
                </>
              )}
            </>
          )}

          {/* Enhanced Card Modal for Mobile */}
          {showModal && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0,0,0,0.5)",
                zIndex: 1050,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "16px",
              }}
              onClick={() => setShowModal(false)}
            >
              <div
                style={{
                  width: "100%",
                  maxWidth: "500px",
                  maxHeight: "90vh",
                  overflowY: "auto",
                  background: "white",
                  borderRadius: "8px",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <InventoryInboundModal
                  index={index}
                  isModalOpen={showModal}
                  handleCloseModal={() => setShowModal(false)}
                  values={selectedResult}
                  setValues={setSelectedResult}
                />
                <div style={{ padding: "16px", textAlign: "center" }}>
                  <Button
                    variant="secondary"
                    onClick={() => setShowModal(false)}
                    style={{ width: "100%" }}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Keep your other existing modals */}
          {showNewItemModal && (
            <InboundNewItemModal
              index={index}
              isModalOpen={showNewItemModal}
              handleCloseModal={handleCloseNewItemModal}
              inventoryItems={selectedResult}
              handleAddInventoryItem={handleAddInventoryItem}
            />
          )}
          {showNotesModal && (
            <InboundNotesModal
              isModalOpen={showNotesModal}
              handleCloseModal={handleCloseNotesModal}
              setFieldValue={(field, value) => {
                const [indexStr, fieldName] = field
                  .replace(/[[\]]/g, "")
                  .split(".");
                const index = parseInt(indexStr);
                const newItems = [...selectedResult];
                (newItems[index] as any)[fieldName] = value;
                setSelectedResult(newItems);
              }}
              values={selectedResult}
              handleNotesUpdate={handleNotesUpdate}
              index={index}
              notesSaved={notesSaved}
            />
          )}
        </Container>
      )}
    </>
  );
};

export default withAuthenticationRequired(InboundLoadPage, {
  onRedirecting: () => <Loading />,
});
